// RESEARCHER_PANEL — Section 10. Reached via 5s long-press on the ASPIRE
// title (Dashboard), the ⚙ icon, or the /#researcher route.

import { useEffect, useState } from 'react';
import { useParticipantStore } from '../store/participantStore';
import {
  verifyPin,
  updateResearcherState,
  registerPush,
  fetchResearcherState,
  describeDevice,
  WorkerApiError,
} from '../worker/api';
import { requestNotificationPermission } from '../notifications/fcm';
import { formatTime, isSleepNextDay } from '../utils/schedule';
import ConfirmationModal from '../components/ConfirmationModal';

const TOTAL_SESSIONS = Number(import.meta.env.VITE_TOTAL_SESSIONS ?? 35);

function PinEntry({ onVerified, onCancel }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const { token } = await verifyPin(pin);
      onVerified(token);
    } catch (err) {
      setError(err instanceof WorkerApiError ? 'Incorrect PIN.' : 'Could not reach the server.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="screen justify-center">
      <h1 className="font-display text-2xl mb-6 text-center">Researcher Access</h1>
      <input
        type="password"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={8}
        value={pin}
        onChange={(e) => setPin(e.target.value)}
        placeholder="PIN"
        className="min-h-touch rounded-xl2 bg-panel border border-hairline px-4 text-lg text-center tracking-[0.5em] mb-4
          focus:border-amber outline-none"
      />
      {error && <p className="text-stop text-sm mb-4 text-center">{error}</p>}
      <button type="button" disabled={busy || !pin} onClick={submit} className="btn-primary mb-3">
        Unlock
      </button>
      <button type="button" onClick={onCancel} className="btn-secondary">
        Cancel
      </button>
    </div>
  );
}

function Panel({ token, onClose }) {
  const participant = useParticipantStore();

  // NOTE ON SESSION NUMBERING: participant_state.session_number counts
  // sessions COMPLETED, which is why the Dashboard shows "Session
  // {session_number + 1}" as what's up next. Exposing the raw counter here was
  // actively misleading — a researcher who reads "this participant is on
  // session 2" means the one they're about to do, not the one behind them.
  // This field is therefore the NEXT session number, and the ±1 conversion
  // happens at the edges, in one place.
  const [participantId, setParticipantId] = useState(participant.participant_id ?? '');
  const [nextSession, setNextSession] = useState(participant.session_number + 1);
  const [contentOverride, setContentOverride] = useState(null); // null = follow the session number
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [wakeHour, setWakeHour] = useState(participant.schedule?.wake_hour ?? 8);
  const [wakeMinute, setWakeMinute] = useState(participant.schedule?.wake_minute ?? 0);
  const [sleepHour, setSleepHour] = useState(participant.schedule?.sleep_hour ?? 22);
  const [sleepMinute, setSleepMinute] = useState(participant.schedule?.sleep_minute ?? 0);
  const [confirmingIdChange, setConfirmingIdChange] = useState(false);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pushStatus, setPushStatus] = useState(null);
  const [serverState, setServerState] = useState(null);

  // What the SERVER thinks, which is what actually determines where reminders
  // go. Only one FCM token is stored per participant, so a second device
  // registering the same ID takes the notifications away from the first — and
  // from the losing phone that looks exactly like push being broken.
  useEffect(() => {
    if (!participant.participant_id) return;
    let cancelled = false;
    fetchResearcherState({ participantId: participant.participant_id, researcherToken: token })
      .then((s) => !cancelled && setServerState(s))
      .catch(() => !cancelled && setServerState({ unreachable: true }));
    return () => {
      cancelled = true;
    };
  }, [participant.participant_id, token, pushStatus]);

  const trimmedId = participantId.trim();
  const idChanged = trimmedId !== (participant.participant_id ?? '');

  // Content index normally just follows the session number — session N uses
  // content item N-1, wrapping at the end of the content set. It's only a
  // separate number at all so a session can be re-run against different
  // material, which is rare enough to live behind "Advanced".
  const derivedContentIndex = ((nextSession - 1) % TOTAL_SESSIONS + TOTAL_SESSIONS) % TOTAL_SESSIONS;
  const effectiveContentIndex = contentOverride ?? derivedContentIndex;

  async function reregisterPush() {
    setPushStatus('requesting');
    const fcmToken = await requestNotificationPermission();
    if (!fcmToken) {
      setPushStatus('failed');
      return;
    }
    await participant.patch({ notifications_enabled: true, fcm_token: fcmToken });
    try {
      await registerPush({
        participantId: participant.participant_id,
        fcmToken,
        schedule: participant.schedule,
      });
      setPushStatus('done');
    } catch {
      setPushStatus('failed');
    }
  }

  async function apply() {
    setBusy(true);
    setStatus(null);

    const schedule = {
      wake_hour: wakeHour,
      wake_minute: wakeMinute,
      sleep_hour: sleepHour,
      sleep_minute: sleepMinute,
      timezone: participant.schedule?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (idChanged) {
      // Switching participant wipes progress AND the schedule, so there is no
      // schedule to send — previously the outgoing participant's wake/sleep
      // times were pushed to the server under the new ID while the device
      // stored null, leaving the two permanently disagreeing.
      await participant.resetForNewParticipant(trimmedId);
    } else {
      await participant.patch({
        session_number: nextSession - 1,
        content_index: effectiveContentIndex,
        schedule,
      });
    }

    let synced = true;
    try {
      await updateResearcherState({
        participantId: trimmedId,
        sessionNumber: idChanged ? 0 : nextSession - 1,
        contentIndex: idChanged ? 0 : effectiveContentIndex,
        schedule: idChanged ? null : schedule,
        researcherToken: token,
      });
    } catch {
      synced = false; // local IndexedDB state is already updated; server sync will lag
    }

    setBusy(false);

    // Close on success so the Dashboard shows the result immediately — a
    // panel that just says "Saved." and sits there gives no evidence anything
    // actually changed, which is exactly how a silently-failing save goes
    // unnoticed. If the server didn't take it, stay put and say so.
    if (synced) onClose();
    else setStatus('offline');
  }

  return (
    <div className="screen">
      <h1 className="font-display text-2xl mb-1">Researcher Panel</h1>

      {/* Current state, read live from the store rather than from the form —
          this is the "did my change actually land?" readout. */}
      <div className="card mb-6">
        <p className="text-xs text-dim">Currently on this device</p>
        <p className="font-display text-xl mt-1">
          {participant.participant_id ?? <span className="text-dim">No participant ID set</span>}
        </p>
        <p className="text-dim text-sm mt-1">
          Up next: session {participant.session_number + 1}
          {' · '}content item {participant.content_index + 1}
          {participant.schedule ? '' : ' · no schedule set'}
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <label className="text-sm text-dim">
          Participant ID
          <input
            value={participantId}
            onChange={(e) => setParticipantId(e.target.value)}
            className="min-h-touch w-full rounded-xl2 bg-panel border border-hairline px-4 text-lg mt-1"
          />
        </label>

        {idChanged ? (
          <div className="card border-amber/40">
            <p className="text-amber text-sm">
              This switches the device to a new participant: progress resets to session 1 and the
              schedule is cleared, so you'll be asked to set a new one.
            </p>
          </div>
        ) : (
          <>
            <label className="text-sm text-dim">
              Next session number
              <input
                type="number"
                min={1}
                max={999}
                value={nextSession}
                onChange={(e) => setNextSession(Number(e.target.value))}
                className="min-h-touch w-full rounded-xl2 bg-panel border border-hairline px-4 text-lg mt-1"
              />
              <span className="block text-xs text-dim/70 mt-1">
                The session the participant will do next. Content follows automatically — item{' '}
                {effectiveContentIndex + 1} of {TOTAL_SESSIONS}.
              </span>
            </label>

            {/* Content index used to sit here as a peer of the session number,
                which invited them to drift apart by accident. It's the same
                number in all normal use, so it's now derived and only
                overridable deliberately. */}
            {!showAdvanced ? (
              <button
                type="button"
                onClick={() => setShowAdvanced(true)}
                className="text-dim/60 text-xs underline self-start"
              >
                Advanced: set content item separately
              </button>
            ) : (
              <label className="text-sm text-dim">
                Content item override (1–{TOTAL_SESSIONS})
                <input
                  type="number"
                  min={1}
                  max={TOTAL_SESSIONS}
                  value={effectiveContentIndex + 1}
                  onChange={(e) => setContentOverride(Number(e.target.value) - 1)}
                  className="min-h-touch w-full rounded-xl2 bg-panel border border-hairline px-4 text-lg mt-1"
                />
                <button
                  type="button"
                  onClick={() => {
                    setContentOverride(null);
                    setShowAdvanced(false);
                  }}
                  className="text-amber text-xs underline mt-1"
                >
                  Follow the session number again
                </button>
              </label>
            )}

            <div className="card">
              <p className="text-sm text-dim mb-2">Wake time</p>
              <div className="flex gap-2">
                <input type="number" min={0} max={23} value={wakeHour} onChange={(e) => setWakeHour(Number(e.target.value))} className="min-h-touch flex-1 rounded-xl2 bg-raised border border-hairline px-3" />
                <input type="number" min={0} max={59} value={wakeMinute} onChange={(e) => setWakeMinute(Number(e.target.value))} className="min-h-touch flex-1 rounded-xl2 bg-raised border border-hairline px-3" />
              </div>
            </div>

            <div className="card">
              <p className="text-sm text-dim mb-2">Sleep time</p>
              <div className="flex gap-2">
                <input type="number" min={0} max={23} value={sleepHour} onChange={(e) => setSleepHour(Number(e.target.value))} className="min-h-touch flex-1 rounded-xl2 bg-raised border border-hairline px-3" />
                <input type="number" min={0} max={59} value={sleepMinute} onChange={(e) => setSleepMinute(Number(e.target.value))} className="min-h-touch flex-1 rounded-xl2 bg-raised border border-hairline px-3" />
              </div>
              {isSleepNextDay(wakeHour, wakeMinute, sleepHour, sleepMinute) && (
                <p className="text-amber text-xs mt-2">
                  This means they sleep at {formatTime(sleepHour, sleepMinute)} — the next day.
                </p>
              )}
            </div>

            <div className="card">
              <p className="text-sm text-dim mb-2">Push notifications</p>

              {/* Which device the SERVER will notify. Only one device can be
                  registered per participant at a time, so this is the answer
                  to "why isn't this phone getting reminders?" */}
              <div className="text-xs mb-3">
                {serverState === null && <p className="text-dim">Checking with the server…</p>}
                {serverState?.unreachable && (
                  <p className="text-amber">Couldn't reach the server to check registration.</p>
                )}
                {serverState && !serverState.unreachable && (
                  <>
                    {serverState.push_registered ? (
                      <>
                        <p className="text-dim">
                          Reminders go to:{' '}
                          <span className="text-paper">{serverState.device_label ?? 'unknown device'}</span>
                        </p>
                        {serverState.device_label !== describeDevice() && (
                          <p className="text-amber mt-1">
                            That isn't this device. Re-register below to move reminders here.
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-stop">
                        No device is registered — this participant will get no reminders.
                        {serverState.token_invalid_at && ' The previous device stopped responding.'}
                      </p>
                    )}
                    <p className="text-dim/60 mt-1">This device: {describeDevice()}</p>
                  </>
                )}
              </div>

              <button type="button" onClick={reregisterPush} disabled={pushStatus === 'requesting'} className="btn-secondary">
                {pushStatus === 'requesting' ? 'Requesting…' : 'Register this device for notifications'}
              </button>
              {pushStatus === 'done' && <p className="text-ok text-sm mt-2">New token registered.</p>}
              {pushStatus === 'failed' && (
                <p className="text-stop text-sm mt-2">
                  Failed — check that notification permission is allowed for this app in your phone's Settings.
                </p>
              )}
            </div>
          </>
        )}
      </div>

      {status === 'offline' && (
        <p className="text-amber text-sm mt-4">
          Saved on this device, but the server couldn't be reached — it will sync later. The readout
          above is up to date.
        </p>
      )}

      <div className="mt-auto pt-6 flex flex-col gap-3">
        <button
          type="button"
          disabled={busy || !trimmedId}
          onClick={() => (idChanged ? setConfirmingIdChange(true) : apply())}
          className="btn-primary"
        >
          {busy ? 'Applying…' : 'Apply'}
        </button>
        <button type="button" onClick={onClose} className="btn-secondary">
          {status === 'offline' ? 'Done' : 'Cancel'}
        </button>
      </div>

      <ConfirmationModal
        open={confirmingIdChange}
        title={`Switch to participant ${trimmedId}?`}
        message="This clears the current session state and resets counters for the new ID. This cannot be undone."
        confirmLabel="Yes, switch participant"
        cancelLabel="No, go back"
        danger
        onCancel={() => setConfirmingIdChange(false)}
        onConfirm={() => {
          setConfirmingIdChange(false);
          apply();
        }}
      />
    </div>
  );
}

export default function ResearcherPanelScreen({ onClose }) {
  const [token, setToken] = useState(null);
  const participantId = useParticipantStore((s) => s.participant_id);

  if (!token) return <PinEntry onVerified={setToken} onCancel={onClose} />;
  // Keyed on the participant ID so switching participants rebuilds the form
  // from scratch. Without this the inputs keep their initial values — set from
  // the OUTGOING participant when the panel mounted — and a second Apply would
  // write the old session number back over the reset.
  return <Panel key={participantId ?? 'none'} token={token} onClose={onClose} />;
}
